import React, { useEffect, useState } from 'react';
import { newNGApi } from '../services/newNGApi';
import { useNewNG } from '../context/NewNGContext';
import styles from '../NewNG.module.css';

interface TopNewsItem {
  id: string;
  title: string;
  isLive: boolean;
  jobId: string;
  airDate?: string;
}

interface TopNewsProps {
  onVideoFocus?: () => void;
}

export const TopNews: React.FC<TopNewsProps> = ({ onVideoFocus }) => {
  const [topNews, setTopNews] = useState<TopNewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { dispatch } = useNewNG();

  // Handle top news item click to change current job
  const handleTopNewsClick = async (item: TopNewsItem) => {
    try {


      // Find the job in the current jobs list or fetch fresh data
      const jobs = await newNGApi.getJobs();
      const targetJob = jobs.find(job => job.job_id === item.jobId);

      if (targetJob) {
        dispatch({ type: 'SET_CURRENT_JOB', payload: targetJob });

        // Focus the video section after job change
        if (onVideoFocus) {
          onVideoFocus();
        }
      } else {
        console.warn('Job not found:', item.jobId);
      }
    } catch (error) {
      console.error('Failed to switch job:', error);
    }
  };

  // Load top news headlines from latest jobs
  useEffect(() => {
    const loadTopNews = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 1. Fetch all available jobs
        const jobs = await newNGApi.getJobs();

        // Sort jobs by creation date (newest first) and take top 8
        const sortedJobs = jobs
          .filter(job => job.created_at) // Only jobs with dates
          .sort((a, b) => {
            const dateA = new Date(a.created_at || '').getTime();
            const dateB = new Date(b.created_at || '').getTime();
            return dateB - dateA; // Descending order (newest first)
          })
          .slice(0, 8); // Top 8 jobs



        // 2. Get headlines for each job
        const newsItems: TopNewsItem[] = [];

        for (let index = 0; index < sortedJobs.length; index++) {
          const job = sortedJobs[index];
          try {
            // Get headlines for this job
            const headlines = await newNGApi.getJobHeadlines(job.job_id);

            if (headlines.length > 0) {
              // Use the first headline as the top news item
              const firstHeadline = headlines[0];

              const newsItem: TopNewsItem = {
                id: `${job.job_id}-${index}`,
                title: firstHeadline.title,
                isLive: false, // Will be determined later based on latest day logic
                jobId: job.job_id,
                airDate: job.created_at
              };

              newsItems.push(newsItem);

            }
          } catch (headlineError) {
            console.warn(`Failed to get headlines for job ${job.job_id}:`, headlineError);
            // Create a fallback news item
            const fallbackItem: TopNewsItem = {
              id: `${job.job_id}-${index}`,
              title: `Breaking News Update from ${job.job_id.slice(0, 8)}`,
              isLive: false,
              jobId: job.job_id,
              airDate: job.created_at
            };
            newsItems.push(fallbackItem);
          }
        }

        // Sort news items by air date (newest first) - they should already be sorted from job sorting
        newsItems.sort((a, b) => {
          const dateA = new Date(a.airDate || '').getTime();
          const dateB = new Date(b.airDate || '').getTime();
          return dateB - dateA; // Descending order (newest first)
        });

        // Mark only items from the latest day as LIVE (same calendar day logic)
        if (newsItems.length > 0) {
          const latestDate = newsItems[0].airDate;
          const latestDateObj = new Date(latestDate || '');

          // Get the start and end of the latest date (same calendar day)
          const latestDateStart = new Date(latestDateObj.getFullYear(), latestDateObj.getMonth(), latestDateObj.getDate());
          const latestDateEnd = new Date(latestDateStart.getTime() + (24 * 60 * 60 * 1000) - 1); // End of same day



          newsItems.forEach((item, index) => {
            const articleTime = new Date(item.airDate || '').getTime();
            const wasLiveBefore = item.isLive;
            // Mark as LIVE only if from the same calendar day as the latest item
            if (articleTime >= latestDateStart.getTime() && articleTime <= latestDateEnd.getTime()) {
              item.isLive = true;
            } else {
              item.isLive = false;
            }

            if (wasLiveBefore !== item.isLive) {

            }
          });
        }


        setTopNews(newsItems);

      } catch (err) {
        console.error('Error loading top news:', err);
        setError('Failed to load top news');

        // Fallback to sample data if backend fails
        const fallbackNews: TopNewsItem[] = [
          { id: '1', title: 'Breaking: Major developments in global markets today', isLive: true, jobId: 'fallback-1' },
          { id: '2', title: 'Technology sector sees unprecedented growth in Q3', isLive: false, jobId: 'fallback-2' },
          { id: '3', title: 'Environmental summit reaches historic agreement', isLive: true, jobId: 'fallback-3' },
          { id: '4', title: 'Healthcare reforms proposed in new legislation', isLive: false, jobId: 'fallback-4' },
          { id: '5', title: 'International trade negotiations show promising progress', isLive: false, jobId: 'fallback-5' },
          { id: '6', title: 'Scientific breakthrough in renewable energy technology', isLive: true, jobId: 'fallback-6' },
          { id: '7', title: 'Education system modernization plans announced', isLive: false, jobId: 'fallback-7' },
          { id: '8', title: 'Sports championship draws record-breaking viewership', isLive: false, jobId: 'fallback-8' }
        ];
        setTopNews(fallbackNews);
      } finally {
        setIsLoading(false);
      }
    };

    loadTopNews();
  }, []);

  if (isLoading) {
    return (
      <div className={styles['newng-top-news-section']}>
        <h2 className={styles['newng-section-title']}>TOP NEWS</h2>
        <div className={styles['newng-news-grid']}>
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className={`${styles['newng-news-item']} ${styles['newng-news-item--loading']}`}>
              <div className={styles['newng-news-number']}>{i + 1}</div>
              <div className={styles['newng-news-content']}>
                <div className={`${styles['newng-news-title']} ${styles['newng-loading-placeholder']}`}>Loading breaking news headlines...</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['newng-top-news-section']}>
        <h2 className={styles['newng-section-title']}>TOP NEWS</h2>
        <div className={styles['newng-error-message']}>
          <div className={styles['newng-error-icon']}>⚠️</div>
          <div className={styles['newng-error-text']}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['newng-top-news-section']}>
      <h2 className={styles['newng-section-title']}>TOP NEWS</h2>
      <div className={styles['newng-news-grid']}>
        {topNews.map((item, index) => (
          <div
            key={item.id}
            className={`${styles['newng-news-item']} ${item.isLive ? styles['newng-news-item--live'] : ''}`}
            onClick={() => handleTopNewsClick(item)}
            style={{ cursor: 'pointer' }}
          >
            <div className={styles['newng-news-number']}>{index + 1}</div>
            <div className={styles['newng-news-content']}>
              <div className={styles['newng-news-title']}>
                {item.title}
              </div>
            </div>
            {item.isLive && (
              <div className={styles['newng-news-live-positioned']}>LIVE</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};